import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Upload, Loader2 } from "lucide-react";

const TEMPLATE = "full_name,email,employee_number,role,stores,primary_store,active\n" +
  "Jane Brown,jane.brown@mailpac.com,EMP-0100,front desk,OHR;PTM,OHR,yes\n" +
  "John Smith,john.smith@mailpac.com,EMP-0101,warehouse,NKG,,yes\n";

const ACTION_STYLE = {
  new: ["New", "#22C55E"], update: ["Update", "#3B82F6"], unchanged: ["No change", "#71717A"], error: ["Error", "#FF3B30"],
};

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "trackboard-staff-template.csv"; a.click();
  URL.revokeObjectURL(url);
};

const PreviewTable = ({ rows }) => (
  <div className="max-h-72 overflow-auto border border-border">
    <table className="w-full text-xs" data-testid="import-preview-table">
      <thead className="sticky top-0 bg-card"><tr className="text-left uppercase text-muted-foreground border-b border-border">
        <th className="p-2">Row</th><th className="p-2">Status</th><th className="p-2">Name / Email</th><th className="p-2">Role</th><th className="p-2">Stores</th><th className="p-2">Details</th>
      </tr></thead>
      <tbody>
        {rows.map((r) => {
          const [label, color] = ACTION_STYLE[r.action];
          return (
            <tr key={r.row} className="border-b border-border align-top" data-testid={`import-row-${r.row}`}>
              <td className="p-2 font-mono-t">{r.row}</td>
              <td className="p-2"><span className="px-2 py-0.5 rounded-full border text-[10px] uppercase" style={{ color, borderColor: color }}>{label}</span></td>
              <td className="p-2"><div className="font-semibold">{r.data.full_name || "—"}</div><div className="text-muted-foreground">{r.data.email}</div></td>
              <td className="p-2">{(r.data.role || "—").replace(/_/g, " ")}</td>
              <td className="p-2 font-mono-t">{r.stores_label || "—"}</td>
              <td className="p-2">{r.errors.length ? <span className="text-alert-red">{r.errors.join("; ")}</span> : r.changes.length ? `Changes: ${r.changes.join(", ").replace(/_/g, " ")}` : ""}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const BulkImportDialog = ({ open, onOpenChange, onDone }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); setPassword(""); if (fileRef.current) fileRef.current.value = ""; };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/users/import/preview", fd); setPreview(data); }
    catch (e) { toast.error(apiError(e)); if (fileRef.current) fileRef.current.value = ""; }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/import/commit", { rows: preview.rows.map((r) => r.raw), default_password: password || null });
      toast.success(`Import done: ${data.created} added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      close(false); onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const c = preview?.counts;
  const nothing = c && c.new + c.update === 0;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-card border-border max-w-4xl" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Import Staff from Spreadsheet</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>Columns: <span className="font-mono-t text-white/80">full_name, email, employee_number, role, stores, primary_store, active</span></p>
          <p>Rows that match an existing employee number (or email) update that person; other rows are added. Roles: warehouse, front desk, supervisor, company admin. Stores: store codes separated by ";" (e.g. OHR;PTM).</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" className="gap-2" onClick={downloadTemplate} data-testid="import-template-btn"><Download size={15} /> Download Template</Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
          <Button className="gap-2" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-choose-file-btn">
            {busy && !preview ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {preview ? "Choose Another File" : "Upload .csv or .xlsx"}
          </Button>
        </div>
        {preview && (
          <>
            <div className="flex flex-wrap gap-4 text-sm" data-testid="import-counts">
              <span className="text-alert-green">{c.new} new</span><span style={{ color: "#3B82F6" }}>{c.update} updates</span>
              <span className="text-muted-foreground">{c.unchanged} unchanged</span><span className="text-alert-red">{c.error} errors (skipped)</span>
            </div>
            <PreviewTable rows={preview.rows} />
            {c.new > 0 && (
              <div>
                <label className="text-xs uppercase text-muted-foreground">Starting password for the {c.new} new employee{c.new > 1 ? "s" : ""}</label>
                <Input className="mt-1 max-w-xs" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="import-default-password" />
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          {preview && (
            <Button onClick={commit} disabled={busy || nothing || (c.new > 0 && password.length < 8)} data-testid="import-confirm-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : `Import ${c.new + c.update} Employee${c.new + c.update === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
